import { jsx as _jsx } from "react/jsx-runtime";
import { Component, useEffect } from 'react';
import { isRouteErrorResponse, useMatches } from 'react-router-dom';
import { useReportClientEvent } from './ClientEventContext';
import { classifyNativeError, nativeErrorBrandsFor } from './nativeErrorBrand';
import { RENDER_FAILURE_EVENT_TYPE, isClientEventRoute, } from './clientTelemetryContract';
// Re-exported (of the import above, not a second specifier) so existing importers
// (this module's own test file) keep resolving it from here; the implementation now
// lives in `nativeErrorBrand.ts`, shared with `unhandledExceptionReporter.tsx` rather
// than duplicated per producer.
export { classifyNativeError };
// Subclasses-before-base order matters (AggregateError extends Error) — see
// `nativeErrorBrand.ts`'s docblock for the full classification rationale.
const NATIVE_BRAND_NAMES = [
    'AggregateError',
    'EvalError',
    'RangeError',
    'ReferenceError',
    'SuppressedError',
    'SyntaxError',
    'TypeError',
    'URIError',
    'Error',
];
const NATIVE_BRANDS = nativeErrorBrandsFor(NATIVE_BRAND_NAMES);
export function renderFailureErrorName(error) {
    return classifyNativeError(error, NATIVE_BRANDS);
}
/**
 * Route attribution comes from MATCHED ROUTE-DEFINITION METADATA — the
 * `handle.telemetryRoute` each route declaration carries — never from
 * `location.pathname` (product round 4, F7, frozen producer rule: pathname is
 * user-controlled text). The router resolves matching, so percent-encoding
 * (`/agents/%6eew`), case (`/AGENTS/NEW`) and normalization (`/settings//`)
 * all attribute to the definition that actually rendered; an unmatched URL
 * matches nothing and the route context is omitted. ⛔ Do not grow a parallel
 * matcher table: a new screen declares its token in its route's handle.
 *
 * The deepest match wins (a section child may override its layout).
 * `isClientEventRoute` is the runtime gate: a stale or untyped table whose
 * handle names a token outside the contract drops the route context instead
 * of emitting it — undeclared means omitted, never guessed.
 */
export function telemetryRouteFromMatches(matches) {
    for (let i = matches.length - 1; i >= 0; i--) {
        // `useMatches()` carries the handle top-level on each match; the raw
        // `router.state.matches` entries nest it under `match.route.handle`. Both
        // shapes occur (the tap reads the latter, the in-router reporter the
        // former), so read either.
        const m = matches[i];
        const handle = (m?.handle ?? m?.route?.handle);
        const token = handle?.telemetryRoute;
        if (typeof token === 'string' && isClientEventRoute(token))
            return token;
    }
    return undefined;
}
/**
 * The producer's declared RANGE — the handle `gen-client-telemetry.mjs` scans
 * for (a computed `route:` value cannot name a single constant at the call
 * site; the emission is bound to this constant via the AST-validated
 * `RANGE.find(...)` shape above). The scan pins range == contract's
 * `context.route`; the app-core route-range test pins the shared tables'
 * handles to their chat-side subset (web-ui pins `agent-detail` on the section
 * route that renders it, desktop pins `bridge` on the sibling). Those joined
 * are the whole proof; the scan alone cannot see runtime values.
 */
export const RENDER_FAILURE_EMITTED_ROUTES = Object.freeze([
    'chat',
    'agents',
    'agent-create',
    'agent-detail',
    'settings',
    'account',
    'bridge',
]);
/**
 * react-router 6.28.0 (pinned) emits this exact message for an unmatched URL
 * on a lazy-bearing table — the ordinary 404 that must not report (F5).
 */
// \x22 = a double quote: a literal one in source breaks the generator's
// string masker (it pairs quotes across the regex and misreads the file).
const NO_ROUTE_MATCH_RE = /^No route matches URL \x22/;
/**
 * Failure-epoch memory per router instance (see tapRouterRenderFailures).
 * Router-keyed so a shell's periodic reporter re-creation re-subscribes to the
 * SAME memory instead of re-reporting retained failures; WeakMap so routers
 * that are garbage-collected take their memory with them.
 */
const routerMemories = new WeakMap();
function routerMemoryFor(router) {
    let memory = routerMemories.get(router);
    if (memory === undefined) {
        memory = new Map();
        routerMemories.set(router, memory);
    }
    return memory;
}
/**
 * Report every NEW error in the router's error state. This is the producer for
 * the error class an element boundary structurally cannot see: `lazy()` route
 * rejections (the stale-chunk-after-deploy case) and loader failures are
 * caught by the ROUTER — before any route element mounts — and surface as
 * `router.state.errors`, rendering the router's error element. No errorElement
 * is defined anywhere in our tables, so that element is react-router's own
 * default fallback; the tap reports WITHOUT touching what the user sees.
 *
 * Ordinary routing responses (`isRouteErrorResponse` — an unmatched URL's 404,
 * a thrown redirect response) are NOT failures and never report (product
 * round 4, F5): reporting them spends the error budget on the router doing
 * its job.
 *
 * Returns the unsubscribe function. Reporting is deferred to the first
 * subscription notification (plus one immediate state read, for an error that
 * landed before this tap subscribed — module-scope routers can already be in
 * an error state at mount time).
 */
export function tapRouterRenderFailures(router, report) {
    // FAILURE-EPOCH memory (architect round 5, blocker 5): keyed by ROUTER (a
    // WeakMap outside the effect, so the 15s bridge-auth reporter re-creation
    // re-subscribes without resetting or re-reporting retained failures), then
    // per error SLOT with the last-reported VALUE compared by Object.is. The
    // epoch is the slot's retained error itself:
    //   - retained re-notification (same slot, same value): silent;
    //   - a different value in the slot (A → B): B reports — and if A returns
    //     afterwards, A reports too (three distinct epochs, three reports);
    //   - the slot or the whole error state clearing drops its memory: the same
    //     value re-failing later is a NEW failure and reports.
    // No history survives the epoch it belongs to.
    const slotMemory = routerMemoryFor(router);
    const reportNew = () => {
        const { errors, location, matches } = router.state;
        if (!errors) {
            // React Router uses `null` when no route is in an error state: no
            // failure is retained anywhere, so no epoch is open.
            slotMemory.clear();
            return;
        }
        const routeToken = telemetryRouteFromMatches(matches);
        // Slots that vanished are cleared failures — drop their epoch memory.
        for (const slot of [...slotMemory.keys()]) {
            if (!(slot in errors))
                slotMemory.delete(slot);
        }
        // ⚠️ STRUCTURAL provenance (architect round 5 blocker 3): the
        // navigational 404 is the one error state where NO route with a real
        // pathname matched — the router never entered app code (measured on the
        // pinned 6.28.0: the match list holds only the synthetic root,
        // pathname ''). Inside that gate the router's own canonical unmatched
        // message is also skipped; the retained value's shape is deliberately
        // NOT assumed here — whatever the pinned router produces under
        // synthetic-root provenance is routing, not a render failure. A genuine
        // application failure always runs under a real matched route, where
        // this gate never opens — even if it throws the same words.
        const noRealMatchedRoute = !matches.some((m) => !!m.pathname);
        for (const [slot, error] of Object.entries(errors)) {
            // The ENTIRE ordinary-response classification is guarded and fails
            // toward REPORTING (product round 6, P2a): a hostile proxy with a
            // throwing `status` getter must produce a report, never silence, and
            // must never replace the original failure with a subscriber throw.
            let ordinary = false;
            try {
                ordinary =
                    isRouteErrorResponse(error) ||
                        (noRealMatchedRoute &&
                            error instanceof Error &&
                            NO_ROUTE_MATCH_RE.test(String(error.message)));
            }
            catch {
                ordinary = false;
            }
            if (ordinary)
                continue;
            // Membership (`has`), not a value comparison: a retained `undefined`
            // must be distinguishable from an empty slot (architect round-8 blocker
            // 4) — otherwise it re-reports on every notification.
            if (slotMemory.has(slot) && Object.is(slotMemory.get(slot), error))
                continue;
            slotMemory.set(slot, error);
            report(error, location.pathname, routeToken);
        }
    };
    const unsubscribe = router.subscribe(() => reportNew());
    reportNew();
    return unsubscribe;
}
/**
 * The router-state half of the producer, as a mountable sibling of
 * `<RouterProvider>` (inside the client-event provider, outside the router —
 * it reads the router OBJECT, it does not render inside it). Renders nothing.
 */
export function RenderFailureRouterTap({ router }) {
    const report = useReportClientEvent();
    useEffect(() => tapRouterRenderFailures(router, (error, _pathname, routeToken) => {
        // Bounded first, branch on it — see the sibling emission above in
        // RenderFailureReporter for the full rationale.
        const bounded = RENDER_FAILURE_EMITTED_ROUTES.find((t) => t === routeToken);
        report({
            eventType: RENDER_FAILURE_EVENT_TYPE,
            errorName: renderFailureErrorName(error),
            ...(bounded !== undefined ? { context: { route: bounded } } : {}),
        });
    }), [router, report]);
    return null;
}
/**
 * Report-and-RETHROW. This boundary is a telemetry tap, not a recovery UI: it
 * forwards the failure to the client-error ingest and then rethrows, so what
 * the user experiences is exactly what an uncaught render error was before
 * this component existed. Recovery would be a product decision (what the
 * fallback says, per surface) and must not arrive smuggled inside a telemetry
 * change; the rethrow is what keeps this MR invisible to every surface's UX.
 */
export class RenderFailureBoundary extends Component {
    componentDidCatch(error) {
        // Every render failure reports — the element boundary sees each render
        // error exactly once (a rethrown render error does not re-enter this
        // boundary), so no dedupe is needed here. The RETHROW is unconditional and
        // rethrows the ORIGINAL object; deduping must never change what escapes.
        this.props.report(renderFailureErrorName(error));
        throw error;
    }
    render() {
        return this.props.children;
    }
}
/**
 * The render-failure producer (first slice of the client-error type set:
 * `decisions`/proposal of 2026-08-29, architect-ruled, gateway-admitted via
 * `gateway-service!301`, unblocked 2026-08-31 when the per-type budget
 * partition merged as `web!513`). Mounted ONCE, wrapping the shared route
 * table's `<Layout />` root, so every shell that imports `routes` gets the tap
 * without per-shell wiring.
 *
 * The route token is resolved here — inside the Router — from the current
 * location, and handed to the boundary as an already-resolved value so the
 * class stays router-agnostic. Budget enforcement is NOT repeated here: the
 * reporter's own per-type partition (capacity 7, 3/sliding-60s for
 * `render_failure`) denies silently by contract, and `componentDidCatch` must
 * stay free of anything that could throw.
 */
export function RenderFailureReporter({ children }) {
    const report = useReportClientEvent();
    // Matched route METADATA (F7), resolved at render time — when a child throws,
    // these are the matches that were actually rendering. Unmatched ⇒ undefined.
    const route = telemetryRouteFromMatches(useMatches());
    return (_jsx(RenderFailureBoundary, { report: (errorName) => {
            // Compute the bounded route FIRST and branch on it (architect round 4,
            // web!515): bounded is a member of the declared range or undefined —
            // by construction, `find` over the range can produce nothing else — so
            // no operator can ever substitute an unfiltered value past it. On a
            // miss the context key is NOT CONSTRUCTED and nothing reaches the
            // wire. The generator's AST check credits exactly this shape.
            const bounded = RENDER_FAILURE_EMITTED_ROUTES.find((t) => t === route);
            report({
                eventType: RENDER_FAILURE_EVENT_TYPE,
                errorName,
                ...(bounded !== undefined ? { context: { route: bounded } } : {}),
            });
        }, children: children }));
}
//# sourceMappingURL=renderFailureReporter.js.map